/**
 * useChannelStatuses — the single source for "is this channel actually working".
 *
 * Composes data the app already has (Genies, their phone numbers, their public
 * pages, and each genie's configured sync_tools) into one shared vocabulary so
 * the strip, the three channel pages and the dashboard REACH band all agree.
 *
 * Where Help Genie Voice genuinely cannot see a channel (the Sync app does not
 * report its run history back here) the channel is marked `unreported` with a
 * reason rather than guessed at. See docs/handoff/channel-status-endpoints.md
 * for what would make those real. Email is real: every Genie's mailbox row is
 * embedded on the agent (`agent.mailbox`) by the agents list endpoint.
 */
import { useMemo } from "react";
import { useUserAgents } from "@/hooks/agent/useAgents";
import { relativeTime } from "@/lib/channels/channelVocabulary";
import type { Agent } from "@/types/agent/agent";
import type { ChannelState, ChannelStatus, ChannelStatusesResult } from "@/types/channels/channel-status";

/** Phone statuses that mean the number is answering right now. */
const LIVE_PHONE_STATUSES = new Set(["active", "in-use"]);

/** The most recent non-null ISO timestamp from a list. */
const latestIso = (values: Array<string | null | undefined>): string | null => {
  const times = values
    .filter((v): v is string => Boolean(v))
    .map((v) => new Date(v).getTime())
    .filter((t) => !Number.isNaN(t));
  if (times.length === 0) return null;
  return new Date(Math.max(...times)).toISOString();
};

/** Voice — a Genie exists and is live, so the web experience answers. */
const buildVoiceChannel = (agents: Agent[]): ChannelStatus => {
  const live = agents.filter((a) => a.is_active && a.is_setup);
  const state: ChannelState = live.length > 0 ? "connected" : "not_connected";
  const lastActivityAt = latestIso(live.map((a) => a.updated_at));

  return {
    key: "voice",
    label: "Voice",
    genieIcon: "microphone",
    state,
    accountLabel: live.length > 0 ? `${live.length} Genie${live.length === 1 ? "" : "s"} live` : null,
    lastActivityAt,
    lastRunOk: live.length > 0 ? true : null,
    detail:
      live.length > 0
        ? `${live.length} Genie${live.length === 1 ? "" : "s"} answering${
            relativeTime(lastActivityAt) ? ` · updated ${relativeTime(lastActivityAt)}` : ""
          }`
        : "No Genie is live yet",
    fixAction:
      live.length > 0
        ? { label: "Manage Genies", to: "/genie-hub/my-genies" }
        : { label: "Build your Genie", to: "/welcome" },
  };
};

/** Phone — a real number attached to a Genie, and whether it is answering. */
const buildPhoneChannel = (agents: Agent[]): ChannelStatus => {
  const phones = agents.map((a) => a.phone).filter((p): p is NonNullable<Agent["phone"]> => Boolean(p));
  const livePhones = phones.filter((p) => LIVE_PHONE_STATUSES.has(p.status ?? ""));
  const pendingPhones = phones.filter((p) => !LIVE_PHONE_STATUSES.has(p.status ?? ""));

  let state: ChannelState = "not_connected";
  if (livePhones.length > 0) state = "connected";
  else if (pendingPhones.length > 0) state = "attention";

  const lastActivityAt = latestIso(phones.map((p) => p.updated_at ?? p.created_at));
  const first = livePhones[0] ?? pendingPhones[0] ?? null;

  let detail = "No number is answering yet";
  if (livePhones.length > 0) {
    detail = `Answering on ${livePhones[0].number ?? "your number"}${
      livePhones.length > 1 ? ` and ${livePhones.length - 1} more` : ""
    }`;
  } else if (pendingPhones.length > 0) {
    detail = `Transfer in progress · up to five business days`;
  }

  return {
    key: "phone",
    label: "Phone",
    genieIcon: "phone",
    state,
    accountLabel: first?.number ?? null,
    lastActivityAt,
    lastRunOk: livePhones.length > 0 ? true : pendingPhones.length > 0 ? false : null,
    detail,
    fixAction:
      livePhones.length > 0
        ? { label: "Manage number", to: "/genie-hub/my-genies" }
        : { label: "Forward your number", to: "/genie-hub/my-genies" },
  };
};

/** Chat — the Genie's public page, which is what a QR code or link opens. */
const buildChatChannel = (agents: Agent[]): ChannelStatus => {
  const pages = agents.filter((a) => Boolean(a.page?.url_name || a.url_name));
  const state: ChannelState = pages.length > 0 ? "connected" : "not_connected";
  const lastActivityAt = latestIso(pages.map((a) => a.page?.updated_at ?? a.updated_at));
  const firstName = pages[0]?.page?.url_name ?? pages[0]?.url_name ?? null;

  return {
    key: "chat",
    label: "Chat",
    genieIcon: "chat",
    state,
    accountLabel: firstName,
    lastActivityAt,
    lastRunOk: pages.length > 0 ? true : null,
    detail:
      pages.length > 0
        ? `${pages.length} shareable page${pages.length === 1 ? "" : "s"} live`
        : "No shareable page yet",
    fixAction:
      pages.length > 0
        ? { label: "Get the QR code", to: "/genie-hub/activation" }
        : { label: "Publish a page", to: "/genie-hub/my-genies" },
  };
};

/**
 * Email — every Genie gets its own mailbox in Help Genie Mail, provisioned when
 * the Genie is created and embedded on the agent as `agent.mailbox`. A
 * provisioned mailbox answers inbound support email from the Genie's knowledge
 * base, so "connected" here means a real address is live. The `email` entry in
 * the connections list is the OUTBOUND notification address, a different thing,
 * so it is not used here.
 */
const buildEmailChannel = (agents: Agent[]): ChannelStatus => {
  const mailboxes = agents.map((a) => a.mailbox).filter((m): m is NonNullable<Agent["mailbox"]> => Boolean(m));
  const live = mailboxes.filter((m) => m.status === "provisioned" && m.email_address);
  const broken = mailboxes.filter((m) => m.status === "pending" || m.status === "failed" || m.status === "unavailable");
  const paused = mailboxes.filter((m) => m.status === "suspended");
  const lastActivityAt = latestIso(mailboxes.map((m) => m.updated_at));

  const state: ChannelState =
    live.length > 0 ? "connected" : broken.length > 0 ? "attention" : "not_connected";

  const brokenAgent = broken.length > 0 ? agents.find((a) => a.mailbox?.id === broken[0].id) : undefined;

  let detail = "No Genie has a mailbox yet";
  if (live.length === 1) {
    detail = `${live[0].email_address} answers this Genie's support email`;
  } else if (live.length > 1) {
    detail = `${live.length} Genies answer their own support email`;
  } else if (broken.length > 0) {
    detail = `${broken[0].agent_name ?? "A Genie"}'s mailbox is ${broken[0].status === "pending" ? "still setting up" : "not working"}`;
  } else if (paused.length > 0) {
    detail = `${paused.length === 1 ? "1 mailbox is" : `${paused.length} mailboxes are`} paused`;
  }

  return {
    key: "email",
    label: "Email",
    genieIcon: "mail",
    state,
    accountLabel: live.length === 1 ? live[0].email_address : live.length > 1 ? `${live.length} mailboxes` : null,
    lastActivityAt,
    lastRunOk: broken.length > 0 ? false : live.length > 0 ? true : null,
    detail,
    fixAction:
      state === "connected"
        ? { label: "Open Help Genie Mail", to: "/genie-hub/channels/mail" }
        : state === "attention"
          ? { label: "Fix mailbox", to: brokenAgent ? `/genie-hub/my-genies/${brokenAgent.id}` : "/genie-hub/my-genies" }
          : { label: "Create a mailbox", to: agents[0] ? `/genie-hub/my-genies/${agents[0].id}` : "/genie-hub/my-genies" },
  };
};

/**
 * Sync — the sync_tools configured across the workspace's Genies. This is a
 * local count from agent.settings, not a live health check against HG Sync,
 * so there is no "attention" state here — a tool is either configured or it
 * isn't.
 */
const buildSyncChannel = (agents: Agent[]): ChannelStatus => {
  const tools = agents.flatMap((a) => a.settings?.sync_tools ?? []);
  const state: ChannelState = tools.length > 0 ? "connected" : "not_connected";

  return {
    key: "sync",
    label: "Sync",
    genieIcon: "sync",
    state,
    accountLabel: tools.length > 0 ? `${tools.length} connected` : null,
    lastActivityAt: null,
    lastRunOk: tools.length > 0 ? true : null,
    detail:
      tools.length > 0
        ? `${tools.map((t) => t.name).slice(0, 2).join(", ")}${tools.length > 2 ? ` and ${tools.length - 2} more` : ""}`
        : "No integrations connected yet",
    fixAction:
      tools.length > 0
        ? { label: "Manage integrations", to: "/genie-hub/channels/sync" }
        : { label: "Connect a tool", to: "/genie-hub/channels/sync" },
  };
};

export const useChannelStatuses = (): ChannelStatusesResult => {
  const { data: agents = [], isLoading: agentsLoading } = useUserAgents();

  const channels = useMemo<ChannelStatus[]>(
    () => [
      buildVoiceChannel(agents),
      buildPhoneChannel(agents),
      buildEmailChannel(agents),
      buildChatChannel(agents),
      buildSyncChannel(agents),
    ],
    [agents],
  );

  return {
    channels,
    isLoading: agentsLoading,
    needsAttention: channels.some((c) => c.state === "attention"),
  };
};
